import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Scale, FileText, ShieldAlert } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const DisclaimerPage = () => {
  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Back Link */}
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#888680] hover:text-[#d9fb06] transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>

        {/* Hero Section */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-12">
          <div className="max-w-4xl">
            <div className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-6 bg-[#d9fb06]/20 text-[#d9fb06]">
              Legal Notice
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#d9fb06] mb-6 tracking-tight">
              Disclaimer
            </h1>
            <p className="text-lg md:text-xl text-[#888680] leading-relaxed">
              Important information about our services, limitations, and your responsibilities.
            </p>
          </div>
        </section>

        {/* Main Content */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="max-w-4xl space-y-8">
            
            {/* Not a Law Firm */}
            <div className="bg-[#252825] border border-[#d9fb06]/30 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <Scale className="w-6 h-6 text-[#d9fb06]" />
                <h2 className="text-xl font-bold text-[#d9fb06]">We Are Not a Law Firm</h2>
              </div>
              <p className="text-[#888680] leading-relaxed mb-4">
                Social Dispute Co. is <strong className="text-[#dfddd6]">not a law firm</strong> and does not provide legal advice, legal representation, or legal services of any kind. We are a dispute preparation and escalation service.
              </p>
              <p className="text-[#888680] leading-relaxed">
                If you need legal advice or representation, you should consult with a licensed professional in your jurisdiction. Nothing on this website or in our communications should be construed as legal advice.
              </p>
            </div>

            {/* No Guarantee */}
            <div className="bg-[#252825] border border-[#3f4816] rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-[#d9fb06]" />
                <h2 className="text-xl font-bold text-[#d9fb06]">No Guarantee of Reinstatement</h2>
              </div>
              <p className="text-[#888680] leading-relaxed mb-4">
                Social Dispute Co. <strong className="text-[#dfddd6]">does not and cannot guarantee</strong> that any social media account will be reinstated, unbanned, or recovered. Platform decisions are made solely by the platforms themselves (Meta, TikTok, Google/YouTube, X Corp., Reddit, Snap Inc., etc.).
              </p>
              <p className="text-[#888680] leading-relaxed">
                We prepare dispute documentation and escalation materials designed to present your case effectively, but the final decision rests entirely with the platform.
              </p>
            </div>

            {/* Compliance */}
            <div className="bg-[#252825] border border-[#3f4816] rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <ShieldAlert className="w-6 h-6 text-[#d9fb06]" />
                <h2 className="text-xl font-bold text-[#d9fb06]">Compliance-First Approach</h2>
              </div>
              <p className="text-[#888680] leading-relaxed mb-4">
                We do not use &quot;hacks,&quot; backchannels, unauthorized access methods, impersonation, or any form of misrepresentation. Our work focuses exclusively on:
              </p>
              <ul className="space-y-2 text-[#888680] ml-4">
                <li>• Clear, factual documentation</li>
                <li>• Procedural review of enforcement actions</li>
                <li>• Lawful escalation pathways</li>
                <li>• Formal correspondence through proper legal channels</li>
              </ul>
            </div>

            {/* Service Scope */}
            <div className="bg-[#252825] border border-[#3f4816] rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-6 h-6 text-[#d9fb06]" />
                <h2 className="text-xl font-bold text-[#d9fb06]">Scope of Services</h2>
              </div>
              <p className="text-[#888680] leading-relaxed mb-4">
                Our services are limited to:
              </p>
              <ul className="space-y-2 text-[#888680] ml-4 mb-4">
                <li>• Preparing notices of dispute</li>
                <li>• Drafting escalation documentation</li>
                <li>• Analyzing platform policies and terms of service</li>
                <li>• Providing guidance on escalation pathways</li>
                <li>• Providing guidance on submission through official channels</li>
              </ul>
              <p className="text-[#888680] leading-relaxed">
                We do not log into your accounts, contact platforms on your behalf using your identity, or take any actions that would violate platform terms of service.
              </p>
            </div>

            {/* No Refund Policy */}
            <div className="bg-[#252825] border border-[#3f4816] rounded-2xl p-8">
              <h2 className="text-xl font-bold text-[#d9fb06] mb-4">Payment and Refunds</h2>
              <p className="text-[#888680] leading-relaxed mb-4">
                Payment is for the preparation and delivery of dispute documentation, not for a guaranteed outcome. Because our service involves significant time and professional resources dedicated to your case, fees are generally non-refundable once work has commenced.
              </p>
              <p className="text-[#888680] leading-relaxed">
                Specific refund policies, if any, will be outlined in your service agreement.
              </p>
            </div>

            {/* Limitation of Liability */}
            <div className="bg-[#252825] border border-[#3f4816] rounded-2xl p-8">
              <h2 className="text-xl font-bold text-[#d9fb06] mb-4">Limitation of Liability</h2>
              <p className="text-[#888680] leading-relaxed mb-4">
                To the fullest extent permitted by law, Social Dispute Co. shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, use, goodwill, or other intangible losses, resulting from:
              </p>
              <ul className="space-y-2 text-[#888680] ml-4">
                <li>• Your use or inability to use our services</li>
                <li>• Any platform&apos;s decision regarding your account</li>
                <li>• Any unauthorized access to or use of our servers or any personal information</li>
                <li>• Any interruption or cessation of transmission to or from our services</li>
              </ul>
            </div>

            {/* Jurisdiction */}
            <div className="bg-[#252825] border border-[#3f4816] rounded-2xl p-8">
              <h2 className="text-xl font-bold text-[#d9fb06] mb-4">Governing Law</h2>
              <p className="text-[#888680] leading-relaxed">
                These terms and any disputes arising from your use of our services shall be governed by and construed in accordance with the laws of the State of Hawaii, United States, without regard to its conflict of law provisions.
              </p>
            </div>

            {/* Last Updated */}
            <div className="text-center pt-8">
              <p className="text-[#888680] text-sm">
                Last Updated: January 2025
              </p>
            </div>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default DisclaimerPage;
